import * as Chart from 'chart.js';
import { format } from 'date-fns';
import { action, computed, observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Line } from 'react-chartjs-2';
import { bind } from '../Utils/Bind';
import { IActiveElement, LineGraph } from './LineGraph';
import { range } from 'lodash';
import { addSeconds, subSeconds } from 'date-fns/esm';
import { displayUtcToZonedTimeFactory } from '../Utils/DisplayDate';
import { Tooltip } from './Tooltip';
import { CommonAppStore } from '../Utils/CommonAppStore';
import { autoinject, inject } from '../Utils/AutoInject';
import { minMax } from '../Utils/Math';
import { BsArrowsAngleContract } from '@react-icons/all-files/bs/BsArrowsAngleContract';
import { BsArrowsAngleExpand } from '@react-icons/all-files/bs/BsArrowsAngleExpand';

export interface ILiveChartProps {
    onDateChange?(v: number): void;
    onExpand?(): void;
    onCollapse?(): void;
    chartRef?(element: typeof Line): void;
    readonly expanded?: boolean;
    readonly className?: string;
    readonly hideLegend?: boolean;
    readonly header?: React.ReactNode;
    readonly chartConfig: Mri.WebApi.ChartConfig;
    readonly displaySerialNumber: string;
    readonly trackDatas: Map<number, Mri.DisplayCharts.TrackDataPoll>;
    readonly startTime: Date;
    readonly endTime: Date;
    readonly firstAvailableSampleTime: Date;
    readonly mobile?: boolean;
}

@autoinject
@observer
export class LiveChart extends React.Component<ILiveChartProps> {
    public render() {
        return (
            <div
                className={
                    this.props.className + ' bg-gray-darker rounded h-100 live-chart-container d-flex flex-column'
                }
            >
                {this.props.header}

                <div className="live-chart-actions">
                    {this.props.onCollapse && this.props.expanded && (
                        <a className="btn btn-link expand-window-button ms-auto" onClick={this.props.onCollapse}>
                            <BsArrowsAngleContract className="text-white" />
                        </a>
                    )}
                    {this.props.onExpand && !this.props.expanded && (
                        <a className="btn btn-link expand-window-button ms-auto" onClick={this.props.onExpand}>
                            <BsArrowsAngleExpand className="text-white" />
                        </a>
                    )}
                </div>

                <LineGraph
                    data={this.chartData}
                    displayLabels={!this.props.mobile && !this.props.hideLegend}
                    hideDots
                    hideLines
                    legendPosition="top"
                    yAxes={this.yAxes}
                    onClick={this.zoomOnData}
                    ignoreAspectRatio
                    onLegendClick={this.legendClick}
                    timeScale
                    enablePan
                    enableZoom
                    fontColor="#fff"
                    gridColor="#fff"
                />
            </div>
        );
    }

    @computed
    private get chartLabels(): Date[] {
        const firstExisting = this.props.chartConfig.trackInfo.find(t => this.props.trackDatas.has(t.varNum));
        if (!firstExisting) return [];

        const utcToZonedTime = displayUtcToZonedTimeFactory(this.props.displaySerialNumber);

        const first = this.props.trackDatas.get(firstExisting.varNum)!;
        const s = first.trackStartTime;
        if (!first?.trackData) return [];
        let labels = range(first.trackData.length).map(i => utcToZonedTime(addSeconds(s, i * first.secsPerSample)));
        
        if (this.windowLargerThanData) {
            const firstDataZero = utcToZonedTime(subSeconds(s, first.secsPerSample));
            labels.unshift(firstDataZero);
            labels.unshift(utcToZonedTime(this.props.startTime));
        }
        
        if (this.props.endTime < this.props.firstAvailableSampleTime) {
            labels = [];
            labels.push(utcToZonedTime(this.props.startTime));
            labels.push(utcToZonedTime(this.props.endTime));
        }
        
        return labels;
    }

    @computed
    private get chartData(): Chart.ChartData<'line'> {
        const dataSets = this.props.chartConfig.trackInfo.map((t, idx) => {
            const newMin = t.uniqueYScale ? t.yScaleMin : this.props.chartConfig.yScaleMin;
            const newMax = t.uniqueYScale ? t.yScaleMax : this.props.chartConfig.yScaleMax;
            const newChartId = `axis-${newMin}-${newMax}`;
            let trackData = toJS(this.props.trackDatas.get(t.varNum)?.trackData) ?? [];

            if (this.windowLargerThanData) {
                trackData.unshift(0);
                trackData.unshift(0);
            }
            if (this.props.endTime < this.props.firstAvailableSampleTime) {
                trackData = [];
                trackData.push(0);
                trackData.push(0);
            }

            const dataSet: Chart.ChartDataset = {
                type: 'line',
                label: t.varName,
                borderColor: this.borderColors[idx],
                backgroundColor: this.borderColors[idx],
                borderWidth: 0.8,
                fill: false,
                data: trackData,
                hidden: this.hiddenLabels.includes(t.varName),
                yAxisID: newChartId,
            };

            return dataSet;
        });

        return {
            labels: this.chartLabels,
            datasets: dataSets,
        };
    }

    @computed
    private get windowLargerThanData() {
        const firstExisting = this.props.chartConfig.trackInfo.find(t => this.props.trackDatas.has(t.varNum));
        if (!firstExisting) return true;

        const first = this.props.trackDatas.get(firstExisting.varNum)!;
        const s = first.trackStartTime;
        const minimumTime = addSeconds(this.props.startTime, first.secsPerSample);
        if (minimumTime < s) {
            return true;
        }
        return false;
    }

    @bind
    @action
    private zoomOnData(activeElements: IActiveElement[], event: any) {
        if (activeElements.length) {
            const clickedDatasetIndex = activeElements[0].datasetIndex;
            const clickedElementindex = activeElements[0].index;

            if (this.chartData.datasets) {
                const filtered = this.chartData.datasets[clickedDatasetIndex];
                this.hiddenLabels = this.chartData.datasets.filter(d => d.label != filtered.label).map(d => d.label!);
                filtered.hidden = true;
            }
        } else if (!this.legendClicked) {
            this.hiddenLabels = [];
        }

        this.legendClicked = false;
    }

    @bind
    @action
    private legendClick(event: any, legendItem: Chart.LegendItem) {
        this.legendClicked = true;

        const legend = legendItem;

        if (legend.hidden) {
            this.hiddenLabels.splice(this.hiddenLabels.indexOf(legend.text!), 1);
        } else {
            this.hiddenLabels.push(legend.text!);
        }
    }

    @computed
    private get yAxes(): { [key: string]: Chart.ScaleOptionsByType<keyof Chart.CartesianScaleTypeRegistry> } {
        const yAxes: { [key: string]: Chart.ScaleOptionsByType<keyof Chart.CartesianScaleTypeRegistry> } = {};

        for (const t of this.props.chartConfig.trackInfo) {
            const newMin = t.uniqueYScale ? t.yScaleMin : this.props.chartConfig.yScaleMin;
            const newMax = t.uniqueYScale ? t.yScaleMax : this.props.chartConfig.yScaleMax;
            const newChartId = `axis-${newMin}-${newMax}`;

            if (!!yAxes[newChartId]) continue;

            yAxes[newChartId] = {
                axis: 'y',
                type: 'linear',
                position: 'left',
                suggestedMin: this.commonAppStore.liveChartStaticScaling ? undefined : newMin,
                suggestedMax: this.commonAppStore.liveChartStaticScaling ? undefined : newMax,
                min: this.commonAppStore.liveChartStaticScaling ? newMin : undefined,
                max: this.commonAppStore.liveChartStaticScaling ? newMax : undefined,
            } as any;
        }

        return yAxes;
    }

    @computed
    private get dataOutOfBounds() {
        return this.props.chartConfig.trackInfo.some(t => {
            const newMin = t.uniqueYScale ? t.yScaleMin : this.props.chartConfig.yScaleMin;
            const newMax = t.uniqueYScale ? t.yScaleMax : this.props.chartConfig.yScaleMax;
            const trackData = this.props.trackDatas.get(t.varNum)?.trackData;
            if (trackData == null) return false;

            const [min, max] = minMax(trackData);

            return min < newMin || max > newMax;
        });
    }

    private borderColors = ['#00FF00', '#FFFF00', '#00FFFF', '#FF00FF', '#099FFF', '#FF0000', '#CC00FF'];
    @observable
    private hiddenLabels: string[] = [];
    @observable
    private legendClicked: boolean = false;
    @observable
    private expanded: boolean = false;
    @inject
    private commonAppStore!: CommonAppStore;
}
